import { createContext, useContext, useMemo, useState, useCallback, useEffect, useRef, ReactNode } from "react";
import { useQueryClient } from '@tanstack/react-query';
import { LifecycleState, LoanData, computeLifecycle, classifyVestaStage } from "@/lib/lifecycle";
import { useLoanQuery, useIncompleteApplications } from "@/hooks/useLoanQuery";
import { useBorrowerLoans } from "@/hooks/useBorrowerLoans";
import { encompassJourney, firstAutoSelectableLoanId } from "@/lib/loanStatus";
import { getCurrentSessionOwnerKey, hasMeaningfulDraftForOwner, readApplicationDraft, updateApplicationDraft } from "@/lib/applicationDraft";
import { useAuthUserOptional } from "@/contexts/AuthContext";
import type { LoanResult } from "@/types/loan";

/** Default empty loan — no hardcoded names or fictional data */
const EMPTY_LOAN: LoanData = {
  respa: {},
  flags: {
    applicationStarted: false,
    applicationComplete: false,
    disclosuresIssued: false,
    loanClosed: false,
    firstPaymentDue: false,
    servicingTransferred: false,
    finalPaymentMade: false,
  },
};

type Ctx = {
  loan: LoanData;
  loanId: string | undefined;
  loanResult: LoanResult | undefined;
  state: LifecycleState;
  setState: (s: LifecycleState) => void;
  setLoan: (l: LoanData) => void;
  /**
   * Pass undefined to clear the active loan (also removes the persisted path-active-loan-id).
   * `explicit` marks a pick the BORROWER made — see the implementation for why only
   * that kind retires a pending "Start a New Loan".
   */
  setActiveLoan: (id: string | undefined, options?: { explicit?: boolean }) => void;
  /**
   * NP-549 AC-5: select a loan the API attached during the login that is completing right
   * now, stamped for the account that just signed in rather than for whoever this provider
   * currently thinks is signed in — see the implementation for why that distinction bites.
   */
  adoptLinkedLoan: (loanId: string, ownerEmail: string) => void;
  /** Call after submission to force lifecycle state re-derivation */
  notifySubmission: () => void;
  isLoading: boolean;
  error: Error | null;
  /** NP-658: the selected loan is owned by Encompass, so PATH offers no write actions on it. */
  isReadOnly: boolean;
};

const LoanContext = createContext<Ctx | null>(null);

export const ACTIVE_LOAN_STORAGE_KEY = 'path-active-loan-id';

const ownerOf = (value: string | null | undefined) => (value ?? '').trim().toLowerCase();

/**
 * NP-529: the persisted loan selection belongs to ONE account.
 *
 * `path-active-loan-id` deliberately outlives a logout (see applicationDraft.ts),
 * and the cross-account sweep in AuthContext only runs when a login can see BOTH
 * the previous and the next owner. Switch accounts any other way — an expired
 * session, or a login where the previous owner was never recorded — and the next
 * borrower restored the PREVIOUS borrower's loan id. Every read then 404'd until
 * something cleared it, which is the "empty until you refresh" in this ticket.
 *
 * Stamping the owner onto the stored value makes that unrepresentable: a selection
 * is only ever restored for the account that made it. A value written before this
 * change carries no owner and is discarded — auto-select then picks the right loan,
 * which is the same path a first-time login already takes.
 */
function readPersistedLoanId(currentOwner: string): string | undefined {
  try {
    // NP-617: "Start a New Loan" clears the selection, but its marker outlives the
    // click — only a successful create clears it. Restoring the previous selection
    // here re-armed the create-once guard against the loan the borrower had just
    // abandoned, and the wizard could then neither INHERIT that id (the intent
    // forbids it) nor CREATE (the guard forbids it): Review resolved no loan id and
    // refused to submit. Nothing recovered it either — the marker survives a reload,
    // a logout (the draft is stashed verbatim) and a Discard, so the borrower's
    // browser stayed wedged while a colleague's worked fine. A pending intent means
    // there is no selection to restore.
    if (readApplicationDraft().newLoanIntent === true) return undefined;
    const raw = localStorage.getItem(ACTIVE_LOAN_STORAGE_KEY);
    if (!raw) return undefined;
    if (!currentOwner || !raw.startsWith('{')) return undefined;

    const stored = JSON.parse(raw) as { owner?: string; loanId?: string };
    return ownerOf(stored?.owner) === currentOwner ? stored.loanId || undefined : undefined;
  } catch (lsErr) {
    // localStorage unavailable (private browsing, quota), or an unparseable value.
    console.warn('[LoanContext] Failed to restore activeLoanId:', lsErr);
    return undefined;
  }
}

/**
 * NP-529: true only when we can positively tell the value belongs to someone else.
 * Unknown ownership stays permitted — see the callers for why refusing those would
 * cost more than it saves.
 */
function isOwnedByAnotherAccount(ownerKey: string | null | undefined, currentOwner: string): boolean {
  const owner = ownerOf(ownerKey);
  return Boolean(owner && currentOwner && owner !== currentOwner);
}

function persistLoanId(id: string | undefined, currentOwner: string): void {
  try {
    if (!id) {
      localStorage.removeItem(ACTIVE_LOAN_STORAGE_KEY);
      return;
    }
    localStorage.setItem(
      ACTIVE_LOAN_STORAGE_KEY,
      JSON.stringify({ owner: currentOwner, loanId: id }),
    );
  } catch (error) {
    console.warn('Failed to persist active loan ID to localStorage:', error);
  }
}

/**
 * Convert an API LoanResult into the lightweight LoanData shape
 * used by the lifecycle state machine.
 *
 * WS9-1 Phase 3 lifecycle contract (API-MIGRATION-SPEC §11):
 *  - New Vesta-direct loans come back `status: "In Progress"` → the FE
 *    lifecycle must be PROSPECT_PARTIAL (started, NOT complete).
 *  - A raw Vesta "Active" status does NOT mean the application is complete —
 *    Vesta loans are "Active" while the borrower is still filling the wizard.
 *    Only an explicit SUBMITTED status / submittedAt (or post-close statuses)
 *    promote the lifecycle past PROSPECT.
 */
export function loanResultToLoanData(result: LoanResult, options?: { isEncompass?: boolean }): LoanData {
  const borrower = result.borrowers?.[0];
  const statusUpper = (result.status ?? '').toUpperCase();
  const isSubmitted = statusUpper === 'SUBMITTED' || Boolean(result.submittedAt) || Boolean(result.confirmationNumber);
  
  // RCA-fix (2026-06-19 v5): Handle thin LoanSummaryResponse from PATH API GET /api/v1/loans/{id}.
  // That endpoint returns { loanId, status:"IN_PROGRESS", estimatedLoanAmount, purpose, ... }
  // which does NOT include borrowers[], loanAmount, or subjectProperty.  Without this fallback
  // all checks below return false → applicationStarted=false → PROSPECT_NO_APP lifecycle →
  // Purchase/Refinance buttons shown for a user who already has a real in-progress loan.
  // Treat status "IN_PROGRESS" / "In Progress" / "IN PROGRESS" as "application started".
  // Also handle estimatedLoanAmount as a fallback for loanAmount (thin response field name).
  const r = result as unknown as Record<string, unknown>;
  const effectiveLoanAmount = result.loanAmount ?? (r.estimatedLoanAmount as number | undefined);
  // Any status that indicates a real loan (not blank/seed) means the application was started
  const statusIndicatesStarted =
    statusUpper === 'IN_PROGRESS' ||
    statusUpper === 'IN PROGRESS' ||    // MapLifecycleToDisplayStatus returns "In Progress"
    statusUpper === 'APPLICATION_IN_PROGRESS' ||
    statusUpper === 'ACTIVE' ||
    statusUpper === 'ACTIVE_DISCLOSED' ||
    statusUpper === 'ACTIVE_NOT_DISCLOSED' ||
    statusUpper === 'SUBMITTED' ||
    statusUpper === 'CLOSED' ||
    statusUpper === 'FUNDED' ||
    statusUpper === 'POST_CLOSE';

  // Check if the user has actually started entering data vs just having an empty seed loan
  const hasRealBorrowerData = borrower && (
    Boolean(borrower.firstName) ||
    Boolean(borrower.lastName) ||
    Boolean(borrower.emailAddress) ||
    Boolean(borrower.phoneNumbers?.length) ||
    Boolean(borrower.taxIdentifier)
  );
  const hasPropertyData = Boolean(result.subjectProperty?.address?.fullStreetAddress) || Boolean(effectiveLoanAmount);
  const hasIncomeAssetData = (result.incomes?.length ?? 0) > 0 || (result.assets?.length ?? 0) > 0;
  
  // User has started the application if they have real data, a real status, OR the loan was submitted
  const applicationStarted = isSubmitted || statusIndicatesStarted || hasRealBorrowerData || hasPropertyData || hasIncomeAssetData;
  
  // NP-72: the Vesta pipeline stage (currentStage) is the authoritative signal
  // for stage-level lifecycle (Prospect vs Active Loan vs Post Close). The
  // `status` field is a within-stage approval verdict (PreQualified…Denied)
  // and can never express "active" or "post-close", so before this mapping no
  // real loan could ever reach an ACTIVE_*/POST_* state — My Home was
  // unreachable (NP-72 QA loop). Unknown/missing stages change nothing.
  const stageClass = classifyVestaStage(result.currentStage);
  const stageActive = stageClass === 'active';
  const stagePostClose = stageClass === 'postClose';

  return {
    respa: {
      borrowerName: borrower
        ? `${borrower.firstName ?? ""} ${borrower.lastName ?? ""}`.trim() || undefined
        : undefined,
      borrowerIncome: result.incomes?.[0]?.qualifiedMonthlyIncome
        ? (result.incomes[0].qualifiedMonthlyIncome * 12)
        : undefined,
      borrowerSSN: borrower?.taxIdentifier || undefined,
      propertyAddress: result.subjectProperty?.address?.fullStreetAddress || undefined,
      estimatedPropertyValue: result.salesContractPurchasePrice || undefined,
      loanAmount: effectiveLoanAmount || undefined,
    },
    flags: {
      applicationStarted: applicationStarted || stageActive || stagePostClose,
      // Phase 3: raw 'ACTIVE' is NOT complete — Vesta reports loans as Active
      // while the application is still in progress. Completion requires an
      // explicit submit marker, a post-close status, or (NP-72) a Vesta
      // pipeline stage past Prospects.
      applicationComplete: isSubmitted || stageActive || stagePostClose || statusUpper === 'CLOSED' || statusUpper === 'FUNDED' || statusUpper === 'POST_CLOSE',
      disclosuresIssued: statusUpper === 'ACTIVE_DISCLOSED',
      // NP-72: Vesta stages Funded/Shipping/Post closing/Purchased mean the
      // loan has closed — promote to the Post-Close lifecycle.
      loanClosed: stagePostClose || statusUpper === 'CLOSED' || statusUpper === 'FUNDED' || statusUpper === 'POST_CLOSE',
      firstPaymentDue: statusUpper === 'POST_CLOSE',
      afnServicing: statusUpper === 'CLOSED' || statusUpper === 'FUNDED',
      servicingTransferred: false,
      finalPaymentMade: false,
      // NP-659: an Encompass loan is never a PATH application in progress — its milestone,
      // through NP-663's table, decides active vs closed. Funded lands on the post-close
      // (Welcome Home) interim view, not PostAfn: nothing says AFN services it.
      ...(options?.isEncompass && {
        applicationStarted: true,
        applicationComplete: true,
        disclosuresIssued: false,
        loanClosed: encompassJourney(result.currentStage).isFunded,
        firstPaymentDue: false,
        afnServicing: false,
      }),
    },
  };
}

function hasValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).some(hasValue);
  return true;
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const digits = String(value ?? '').replace(/[^\d.]/g, '');
  if (!digits) return undefined;
  const parsed = Number(digits);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function hasStepDraftData(draft: Record<string, unknown>, ownerKey: string): boolean {
  // Scope to the current borrower only; prevents cross-account stale draft signals.
  if (!hasMeaningfulDraftForOwner(draft, ownerKey)) return false;
  return Boolean(draft.lastStepKey || draft.furthestStepKey || draft.eConsentSigned);
}

function draftToLoanData(): LoanData | null {
  const draft = readApplicationDraft();
  const ownerKey = getCurrentSessionOwnerKey();
  // New/other account with no owned draft should never inherit local state.
  if (!hasMeaningfulDraftForOwner(draft, ownerKey)) return null;

  const personal = (draft.personal ?? {}) as Record<string, unknown>;
  const income = (draft.income ?? {}) as Record<string, unknown>;
  const property = (draft.property ?? {}) as Record<string, unknown>;
  
  // More careful detection of meaningful draft data
  const hasPersonalData = personal && (hasValue(personal.firstName) || hasValue(personal.lastName) || hasValue(personal.email) || hasValue(personal.ssn));
  const hasIncomeData = income && Object.values(income).some(hasValue);
  const hasPropertyData = property && (hasValue(property.propertyAddress) || hasValue(property.purchasePrice) || hasValue(property.loanAmount) || hasValue(property.loanPurpose));
  const hasOtherData = hasValue(draft.residency) || hasValue(draft.assets);
  const hasStepData = hasStepDraftData(draft as Record<string, unknown>, ownerKey);
  
  const hasMeaningfulDraft = hasPersonalData || hasIncomeData || hasPropertyData || hasOtherData || hasStepData;

  // Phase 3: the `path-submitted-application` snapshot is RETIRED — submission
  // state comes from the server (loan status/submittedAt via loanResultToLoanData).
  // The local draft's own submit markers remain a fast-path display hint only —
  // and only beside the loan they were written for. A marker with no loanId has no
  // server loan behind it (the 404 handler below strips the id of a loan this
  // borrower cannot read), and promoting it put a borrower with no application on
  // the ACTIVE_NOT_DISCLOSED dashboard with a My Loan that never loads (NP-715).
  const hasSubmitMarker = draft.applicationStatus === 'submitted' || Boolean(draft.submittedAt);
  const isSubmitted = Boolean(draft.loanId) && hasSubmitMarker;

  // If the draft says submitted but has no meaningful data (e.g. step data
  // cleared), still surface a minimal "submitted" LoanData until the server
  // read resolves.
  if (isSubmitted && !hasMeaningfulDraft) {
    return {
      respa: {},
      flags: {
        applicationStarted: true,
        applicationComplete: true,
        disclosuresIssued: false,
        loanClosed: false,
        firstPaymentDue: false,
        servicingTransferred: false,
        finalPaymentMade: false,
      },
    };
  }

  if (!hasMeaningfulDraft) return null;

  const borrowerEmployments = Array.isArray(income.borrowerEmployments)
    ? income.borrowerEmployments
    : Array.isArray(income.employers)
      ? income.employers
      : Array.isArray(income.employments)
        ? income.employments
        : [];
  const firstEmployment = borrowerEmployments[0] as Record<string, unknown> | undefined;

  return {
    respa: {
      borrowerName: [personal.firstName, personal.lastName].filter(hasValue).join(' ').trim() || undefined,
      borrowerIncome: toNumber(firstEmployment?.income) ?? toNumber(firstEmployment?.monthlyGrossIncome),
      borrowerSSN: typeof personal.ssn === 'string' ? personal.ssn : undefined,
      propertyAddress: typeof property.propertyAddress === 'string' ? property.propertyAddress : undefined,
      estimatedPropertyValue: toNumber(property.purchasePrice) ?? toNumber(property.estimatedPropertyValue),
      loanAmount: toNumber(property.loanAmount),
    },
    flags: {
      applicationStarted: hasMeaningfulDraft,
      applicationComplete: isSubmitted,
      disclosuresIssued: false,
      loanClosed: false,
      firstPaymentDue: false,
      servicingTransferred: false,
      finalPaymentMade: false,
    },
  };
}

export const LoanProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  // Track whether we are mid-404-clear to prevent isFreshUser from firing prematurely.
  // When LoanContext detects a 404 and clears the stale activeLoanId, there is a React
  // render window where: activeLoanId=undefined, incompleteApps=[] (cached empty),
  // storedLoanId=null (just removed).  Without this guard setIsFreshUser(true) fires,
  // showing the Purchase/Refinance screen even though the user has a valid loan in DB.
  // The flag is set on 404 detection and cleared once incompleteApps refetch resolves.
  const isStaleIdClearingRef = useRef(false);
  // NP-431: loan ids that already 404'd. The sole-loan auto-select below reads
  // GET /api/v1/loans, whose 5-minute cache can still list a loan that
  // GET /loans/{id} just 404'd — re-selecting it would spin a
  // 404 → clear → auto-select loop (the same hazard the draft-restore effect
  // guards against by clearing applicationDraft.loanId).
  // Deliberately STATE, not a ref: adding an id must re-run the sole-loan memo
  // below. That is the NP-431 stale-selection fallback — when a persisted
  // selection turns out to be invalid (404) we DO fall back to auto-select,
  // provided exactly one reachable loan remains. With a ref the recompute would
  // not happen until some unrelated re-render.
  const [stale404LoanIds, setStale404LoanIds] = useState<ReadonlySet<string>>(() => new Set());

  // Dynamically resolve the active loan ID from the user's applications
  // Restore from localStorage if available
  /**
   * NP-529: ONE notion of "who is signed in", read through CONTEXT rather than
   * storage — that subscription is what makes an account switch observable here.
   * AuthProvider takes `children` as props, so a login re-renders AuthProvider
   * while React skips the identical LoanProvider element; anything this provider
   * reads from storage during render is simply never re-read. Storage stays as
   * the fallback for the pre-auth render and for tests that mount this alone.
   */
  const authUser = useAuthUserOptional();
  const sessionOwner = ownerOf(authUser?.email ?? getCurrentSessionOwnerKey());

  const [activeLoanId, setActiveLoanIdState] = useState<string | undefined>(
    () => readPersistedLoanId(sessionOwner),
  );

  // Custom setter that persists to localStorage
  const setActiveLoanId = useCallback((id: string | undefined) => {
    setActiveLoanIdState(id);
    persistLoanId(id, sessionOwner);
  }, [sessionOwner]);
  // Track when we've determined this is a fresh user (no loan data exists)
  const [isFreshUser, setIsFreshUser] = useState<boolean>(false);


  // Fetch the borrower's incomplete/active applications to discover their loan ID
  // Make this resilient to API failures for fresh users and mixed-mode scenarios
  const { data: incompleteApps, isLoading: isLoadingApps, error: incompleteAppsError } = useIncompleteApplications();

  // NP-431: the LoanSwitcher's source of truth — GET /api/v1/loans (ALL loans),
  // gated by the same isSwitcherVisibleStatus allowlist the switcher uses. This
  // is deliberately a DIFFERENT (wider) source than incompleteApps above:
  // /api/v1/loans/incomplete only returns loans with no SubmittedDate inside a
  // 6-month window, so a submitted-or-beyond loan is absent from it while still
  // being selectable in the switcher. TanStack dedupes this by queryKey, so
  // sharing the switcher's query costs no extra request.
  const { data: borrowerLoansResult, isLoading: isLoadingBorrowerLoans } = useBorrowerLoans();

  /**
   * NP-431/NP-600: the id of the loan to adopt when the borrower has no selection.
   * The eligibility rule lives in ONE place — `firstAutoSelectableLoanId` in
   * @/lib/loanStatus — so the still-open BA question about partial/dead/archived
   * loans is cheap to answer later.
   *
   * NP-600 widened this from "only when there is exactly one" to "the first
   * eligible": with 2+ loans nothing was adopted, so `useLoanQuery(undefined)`
   * never fetched, the lifecycle fell to PROSPECT_NO_APP, and My Loan and Contacts
   * vanished — while the switcher displayed `loans[0]` anyway. See the helper for
   * why that is a reversal of NP-70's "never guess" in name only.
   *
   * `stale404LoanIds` is in the dep list so that clearing a 404'd loan re-runs
   * this: if another loan just became the newest reachable one, it should be
   * adopted (see the stale-selection fallback below).
   */
  const autoSelectLoanId = useMemo(
    () =>
      firstAutoSelectableLoanId(borrowerLoansResult?.loans ?? [], {
        excludeLoanIds: stale404LoanIds,
      }),
    [borrowerLoansResult, stale404LoanIds],
  );

  // NP-70's "Start a New Loan" marks intent to begin a fresh application.
  //
  // Read at RENDER TIME, deliberately NOT inside a useMemo: the marker lives in
  // localStorage, which cannot be a reactive dependency, so a memo would cache
  // a value that silently goes stale. Reading it per render means the value is
  // always correct for the render in progress (readApplicationDraft is a small
  // synchronous JSON.parse, and this provider does not re-render hot).
  const newLoanIntentActive = readApplicationDraft().newLoanIntent === true;

  /**
   * NP-600: the loan this account's own draft names, if any — read at render time for
   * the same reason as the marker above.
   *
   * Both the draft-restore effect and the adoption effect need it, and they must not
   * disagree: they run in the same commit, so the adoption effect still sees
   * `activeLoanId` undefined from its closure even after the restore has queued its
   * update. Before adoption widened to the first eligible loan that was harmless (with
   * 2+ loans nothing was adopted); now it would overwrite a resumed draft with the
   * newest loan. Deriving it once, here, is what keeps the precedence single-valued:
   * a draft's own loan always wins over a blind adoption.
   */
  const draftLoanIdForOwner = (() => {
    const draft = readApplicationDraft();
    if (isOwnedByAnotherAccount(draft.draftOwnerKey, sessionOwner)) return undefined;
    return draft.loanId;
  })();

  /**
   * NP-614: `pendingAutoSelectLoanId` and `pendingIncompleteAppPickId` (below) are
   * both intent-aware since NP-617 — while "Start a New Loan" stands, neither hands
   * the wizard a loan to inherit. This sibling read the same `draftLoanIdForOwner`
   * directly and was never covered by that fix: a loan the draft named BEFORE the
   * marker was set (or one written back by the resume-adopt branch on an earlier,
   * since-abandoned attempt) survived `clearApplicationDraft()` having run since —
   * `newLoanIntent` and `loanId` are independent fields, so nothing forces the
   * marker to imply an empty `loanId`. Adopting it here re-arms the create-once
   * guard against a loan the intent says to abandon, and Review resolves no id to
   * submit — the exact deadlock NP-617 closed on the other two paths.
   */
  const pendingDraftLoanIdForOwner = newLoanIntentActive ? undefined : draftLoanIdForOwner;

  /**
   * NP-431 — THE single "should a loan be auto-adopted, and which?" value. All
   * three gates below (auto-select, fresh-user, loading) MUST read this one
   * value rather than re-deriving the intent themselves.
   *
   * Review fix (DOZER/Copilot, PR #79): they previously disagreed. The
   * auto-select effect honoured `newLoanIntent` while the fresh-user effect and
   * `hasUnresolvedLoan` only looked at the sole loan, so "Start a New Loan" on a
   * single-loan account left activeLoanId undefined AND isFreshUser false AND
   * hasUnresolvedLoan true — a skeleton that never resolved. Folding the intent
   * into the value itself makes that state unrepresentable.
   */
  const pendingAutoSelectLoanId = newLoanIntentActive ? undefined : autoSelectLoanId;

  useEffect(() => {
    // NP-529: the draft carries a loanId too, and `readApplicationDraft` does not
    // scope by owner — so a draft left behind by another account handed over THEIR
    // loan id and 404'd exactly like the persisted selection did. Only refuse when
    // the owners are known to differ: a draft written before owner stamping has no
    // key, and refusing those would strand a legitimate resume. That ownership rule
    // now lives in `draftLoanIdForOwner` so adoption cannot read it differently.
    // NP-614: gated through `pendingDraftLoanIdForOwner`, not the raw value — see
    // its definition for why the raw draft loanId is not safe to adopt on its own.
    if (pendingDraftLoanIdForOwner && !activeLoanId) {
      setActiveLoanId(pendingDraftLoanIdForOwner);
    }
    // setActiveLoanId is keyed on sessionOwner and persists to an owner-scoped key, so
    // omitting it lets a stale closure write the selection under the PREVIOUS owner —
    // the cross-account leak NP-529 fixed. Every sibling effect already lists it.
  }, [activeLoanId, pendingDraftLoanIdForOwner, setActiveLoanId]);

  // Set activeLoanId from the first incomplete application once fetched
  // Gracefully handle empty results (which could be due to API failures)
  useEffect(() => {
    if (incompleteApps && incompleteApps.length > 0 && !activeLoanId) {
      // Don't auto-pick from a list of 2+ candidates — the user selects via the
      // LoanSwitcher ("Option A"). NP-431: a SOLE loan is adopted automatically
      // by the effect below (NP-70 AC: "If borrower has 1 loan: static display").
      setIsFreshUser(false);
    }
    
    // Handle fresh user scenario - when incomplete apps query completes with empty results
    // BUT check draft storage BEFORE marking user as "fresh".
    // RCA-fix (2026-06-19 rev2): also guard against marking fresh when path-active-loan-id
    // is present in localStorage.  That key is no longer cleared on logout (see
    // applicationDraft.ts DRAFT_LIFECYCLE_KEYS change) so a returning user whose loan
    // was loaded by activeLoanId will set activeLoanId in the useState initialiser — this
    // guard prevents a window where both activeLoanId state AND incompleteApps are still
    // loading from triggering the fresh-user branch prematurely.
    // NP-529: only THIS account's persisted selection counts — another borrower's
    // leftover id must not suppress the fresh-user determination.
    const storedLoanId = readPersistedLoanId(sessionOwner) ?? null;
    // NP-431: a borrower whose only loan is already SUBMITTED has an empty
    // incompleteApps, so this branch used to mark them "fresh" and flash the
    // Purchase/Refinance screen. Wait for GET /api/v1/loans, and stand down
    // entirely once it reports a loan the effect below will adopt.
    // NOTE: this reads pendingAutoSelectLoanId, NOT autoSelectLoanId — after "Start a
    // New Loan" nothing will be adopted, so this branch MUST run and mark the
    // borrower fresh, otherwise the loading gate never settles.
    if (isLoadingBorrowerLoans || pendingAutoSelectLoanId) {
      return;
    }
    if (!isLoadingApps && (!incompleteApps || incompleteApps.length === 0) && !activeLoanId && !storedLoanId) {
      // GUARD: do NOT mark as fresh while we are mid-404-clear (a stale loanId was just
      // removed and an incompleteApps refetch is in flight).  Without this guard the window
      // between setActiveLoanId(undefined) and the refetch resolving triggers isFreshUser=true
      // which shows the Purchase/Refinance screen even though the user has a real loan.
      // NP-529: another account's draft is not backup data for this one.
      const ownDraft = readApplicationDraft();
      const draftLoanId = isOwnedByAnotherAccount(ownDraft.draftOwnerKey, sessionOwner) ? undefined : ownDraft.loanId;

      // RCA-fix (2026-06-19 v4): check draft BEFORE the stale-clearing guard.
      // If incompleteApps returns [] after a 404-clear AND there's no draft loan,
      // the user's account is genuinely empty (loans were cleaned up).  Without this
      // check the guard would suppress isFreshUser=true indefinitely and leave the app
      // stuck on a blank PROSPECT_NO_APP screen instead of the correct fresh-user landing.
      if (isStaleIdClearingRef.current && !draftLoanId) {
        // The 404-cleared loan was the only loan; no backup data exists.  This is a
        // genuine fresh-user scenario after cleanup.  Safe to clear the flag and proceed.
        console.info('[LoanProvider] 404-clear cycle completed with empty API + empty draft — treating as fresh user');
        isStaleIdClearingRef.current = false;
      } else if (isStaleIdClearingRef.current) {
        console.info('[LoanProvider] Suppressing isFreshUser=true during 404-clear cycle — waiting for incompleteApps refetch');
        return;
      }

      // Only mark as fresh user if NO loan data exists in draft storage OR API
      if (!draftLoanId) {
        // Clear the stale-clearing flag: the refetch settled with no loans, so this is
        // genuinely a fresh user (or the old loans were cleaned up).  Safe to proceed.
        isStaleIdClearingRef.current = false;
        setIsFreshUser(true);
      }
    }
    
    // If incompleteApps query completed (even with empty results due to errors),
    // and we still don't have a loan ID, that's fine - fresh users start without one
  }, [incompleteApps, activeLoanId, isLoadingApps, isLoadingBorrowerLoans, pendingAutoSelectLoanId]);

  // Loan resolution removed — "Option A" requires explicit selection via the
  // LoanSwitcher when there are 2+ candidates. NP-431 restores automatic
  // resolution for the unambiguous 1-loan case only.

  // Dev/demo override — when the state switcher sets a state, we use seeds
  const [overrideState, setOverrideState] = useState<LifecycleState | null>(null);
  const [overrideLoan, setOverrideLoan] = useState<LoanData | null>(null);

  /**
   * NP-529 — the account switch itself.
   *
   * LoanProvider is mounted once at App level, above the router, so it does NOT
   * remount on login: signing out and back in as someone else is plain SPA
   * navigation. `activeLoanId` therefore kept the PREVIOUS borrower's loan id in
   * React state, and every read 404'd. Owner-stamping the stored value could not
   * help — the initialiser that reads it only ever ran once, at app start, which
   * is exactly why refreshing "fixed" it.
   *
   * Watching the signed-in owner re-reads the selection for whoever is signed in
   * now: their own persisted loan, or nothing, which auto-select then resolves.
   */
  const [lastSessionOwner, setLastSessionOwner] = useState(sessionOwner);
  if (lastSessionOwner !== sessionOwner) {
    // Adjusted DURING render, not in an effect. An effect runs after the commit,
    // so the first render under the new account would still hold the previous
    // borrower's id — long enough to fire exactly the 404 this fixes. Setting
    // state here makes React re-render before committing, so nothing downstream
    // ever observes the old id.
    console.info('[LoanContext] NP-529: session owner changed — re-reading the loan selection');
    setLastSessionOwner(sessionOwner);
    // Deliberately the raw setter: this adopts the new owner's stored value, it
    // does not write one. Overrides belong to the account that set them.
    setActiveLoanIdState(readPersistedLoanId(sessionOwner));
    setIsFreshUser(false);
    setOverrideLoan(null);
    setOverrideState(null);
  }

  // NP-234/NP-235/NP-233: Bumped after submission to force apiLoan memo re-derivation
  const [submissionTrigger, setSubmissionTrigger] = useState(0);

  // Fetch real loan data from API (MSW in dev)
  const { data: loanResult, isLoading: isLoadingLoan, error } = useLoanQuery(activeLoanId);

  // P0 FIX — Clear stale activeLoanId on 404 so the app doesn't stay permanently broken.
  // Root cause: James Chen's activeLoanId in localStorage points to a loan that belongs
  // to a different borrower (GUID drift between DevMockAuthMiddleware and DB).  The API
  // returns 404 for any GET/PATCH on that loan, causing "Unable to load application".
  // Fix: when we get a 404, clear the stale ID so LoanContext falls back to loan selection
  // or auto-selects the most recent valid loan (see effect below).
  //
  // RCA-fix (2026-06-19 v3): Also clear the applicationDraft.loanId to prevent the draft
  // effect from immediately restoring the stale ID on the very next render, which would
  // create an infinite 404 → clear → restore loop.  Also invalidates the incompleteApps
  // query so the borrower's real loans are fetched fresh (not from the 60s stale cache)
  // after the stale ID is cleared.  The isStaleIdClearingRef flag blocks setIsFreshUser
  // from firing during the brief window between the 404 clear and the refetch.
  useEffect(() => {
    if (!error || !activeLoanId) return;
    const apiErr = error as { status?: number };
    if (apiErr.status === 404) {
      console.info('[LoanContext] 404 on activeLoanId', activeLoanId, '— clearing stale loan ID + draft + cache');
      isStaleIdClearingRef.current = true;
      // NP-431: remember it so the sole-loan auto-select cannot re-pick it from
      // the cached GET /api/v1/loans list and loop, then force the sole-loan
      // memo to recompute so a remaining reachable loan can be adopted.
      setStale404LoanIds((prev) => new Set(prev).add(activeLoanId));
      setActiveLoanId(undefined);
      // Clear stale loanId from the application draft so the draft-restore effect
      // (draftLoanId && !activeLoanId → setActiveLoanId(draftLoanId)) does not re-add it.
      updateApplicationDraft((current) => ({ ...current, loanId: undefined }));
      // Force-refetch incomplete apps so auto-select can pick up the user's real loans.
      void queryClient.invalidateQueries({ queryKey: ['loans', 'incomplete'] });
    }
  }, [error, activeLoanId, setActiveLoanId, queryClient]);

  // P1 FIX (v2) — Auto-select the best incomplete loan when there is no activeLoanId.
  // Root cause of Issue 4 (Purchase/Refinance re-appearing):
  //   Duplicate empty PROSPECT_NO_APP loans existed from test API calls.  Sorting purely by
  //   updatedAt DESC picked the newest EMPTY loan → applicationStarted=false → PROSPECT_NO_APP
  //   lifecycle → Purchase/Refinance buttons shown even though a real draft existed.
  // Fix: prioritise IN_PROGRESS/APPLICATION_IN_PROGRESS loans over PROSPECT_NO_APP/In Progress;
  //   within the same priority tier, sort by most-recently-updated desc.
  /**
   * The P1 pick, derived rather than computed inside its effect.
   *
   * NP-600: the adoption effect below has to know whether this path is going to select
   * something, and it cannot learn that from `activeLoanId` — both effects read the same
   * stale value within one commit, so both would fire and the later one would win. That
   * is the race already fixed for draft precedence via `draftLoanIdForOwner`; this is the
   * same shape for the same reason.
   *
   * It matters because the two picks genuinely disagree. This one prioritises an
   * IN_PROGRESS incomplete application (the "Purchase/Refinance re-appearing" fix);
   * adoption takes the most recently updated switcher-eligible loan, which may be a
   * SUBMITTED one. A borrower with an in-progress application and a newer submitted loan
   * must land on the application they were filling in, not the submitted loan.
   */
  const incompleteAppPickId = useMemo(() => {
    if (!incompleteApps || incompleteApps.length === 0) return undefined;

    const statusPriority = (status?: string): number => {
      const s = (status ?? '').toLowerCase();
      if (s === 'in progress' || s === 'application_in_progress' || s === 'in_progress') return 0;
      return 1; // PROSPECT_NO_APP / empty loans get lower priority
    };

    const sorted = [...incompleteApps].sort((a, b) => {
      const aPri = statusPriority(a.status);
      const bPri = statusPriority(b.status);
      if (aPri !== bPri) return aPri - bPri; // prioritise IN_PROGRESS
      // Within the same tier, sort by most-recently-updated descending
      const aTime = new Date(a.updatedAt ?? a.lastUpdated ?? 0).getTime();
      const bTime = new Date(b.updatedAt ?? b.lastUpdated ?? 0).getTime();
      return bTime - aTime;
    });
    // The id, not the row: a candidate carrying no loanId was never selectable here, and
    // must not suppress adoption either. Reading `sorted[0]` keeps P1's own behaviour
    // byte-for-byte what it was — only where the decision lives has changed.
    return sorted[0]?.loanId ?? undefined;
  }, [incompleteApps]);

  /**
   * NP-617 — the incomplete-apps sibling of `pendingAutoSelectLoanId`.
   *
   * The P1 effect below was the ONE selection path that read its pick directly
   * instead of through an intent-aware value, so "Start a New Loan" was honoured
   * everywhere except here: this effect re-selected the borrower's previous
   * in-progress loan and re-persisted it, which re-armed the create-once guard
   * against the very loan the intent had just abandoned. Submit then had no id to
   * resolve (see `readPersistedLoanId`). Deriving it once, here, is what keeps the
   * readers single-valued — the same reason `pendingAutoSelectLoanId` exists.
   */
  const pendingIncompleteAppPickId = newLoanIntentActive ? undefined : incompleteAppPickId;

  useEffect(() => {
    if (activeLoanId || !incompleteApps || incompleteApps.length === 0) return;
    // Clear the 404-clearing flag now that incompleteApps resolved with results.
    isStaleIdClearingRef.current = false;

    if (pendingIncompleteAppPickId) {
      console.info('[LoanContext] Auto-selecting the best incomplete loan:', pendingIncompleteAppPickId);
      setActiveLoanId(pendingIncompleteAppPickId);
      setIsFreshUser(false);
    }
  }, [incompleteApps, pendingIncompleteAppPickId, activeLoanId, setActiveLoanId]);

  // NP-431/NP-600 — Adopt a loan when the borrower has no selection.
  //
  // Root cause of the bug (uploaded documents blank until the user opens the
  // switcher): the P1 effect above is the only auto-select, and it reads
  // `incompleteApps` (GET /api/v1/loans/incomplete), which excludes any loan
  // that has a SubmittedDate. QA's scenario — a single loan already opened in
  // Vesta with a document uploaded — is submitted-or-beyond, so it is absent
  // from that list, nothing auto-selects, and activeLoanId stays undefined.
  // Documents then calls useDocuments(undefined), which silently falls back to
  // the borrower-level (orphaned) endpoint instead of the loan-scoped one, so
  // "Uploaded Documents" renders empty with no error. Picking the loan by hand
  // set activeLoanId and the documents appeared.
  //
  // Relationship to NP-70: NP-70's AC (BA Paolo Domingo) already REQUIRES this —
  // "If borrower has 1 loan: static display with '+Start a New Loan' CTA", and
  // "Selecting a different loan triggers a full context switch — dashboard,
  // documents, conditions, and all loan-specific views update". A static display
  // of the sole loan means it is the active loan with no selection interaction.
  // The "explicit selection required" phrasing in the older single-loan test was
  // a developer-side deviation from that AC, not a product decision.
  //
  // NP-600 then extended this to 2+ loans, so NP-70's "the app never guesses" no longer
  // holds — and it never did in practice. LoanSwitcher renders
  // `loans.find(active) ?? loans[0]`, so a borrower with no selection was ALREADY shown
  // the first loan while the app held none: no loan fetch, lifecycle PROSPECT_NO_APP, and
  // My Loan and Contacts gone. Adoption takes the first eligible loan, which is the same
  // element the switcher displays. NP-70's dropdown is untouched — switching still works
  // exactly as its 2+ AC describes; what changed is that the state now agrees with the
  // header instead of contradicting it.
  useEffect(() => {
    // pendingAutoSelectLoanId already encodes NP-70's "Start a New Loan" intent
    // (it resolves to undefined while that marker is set), so this effect must
    // NOT re-check the draft itself — that split is exactly what produced the
    // permanent-skeleton bug DOZER/Copilot caught.
    // NP-600: the draft's own loan wins, and so does the P1 incomplete pick — both of
    // those effects are already selecting, and neither's `setActiveLoanId` is visible
    // here within the same commit. Precedence: resumed draft, then the in-progress
    // application, then the newest eligible loan.
    //
    // `isLoadingApps` is part of that precedence, not a nicety: /loans and
    // /loans/incomplete are independent queries, so /loans can resolve first and leave
    // `incompleteAppPickId` undefined merely because the other request is still in
    // flight. Adopting then would set activeLoanId, and the P1 effect bails on it — the
    // in-progress application would lose to whichever response happened to arrive first.
    if (
      activeLoanId ||
      isLoadingApps ||
      pendingDraftLoanIdForOwner ||
      incompleteAppPickId ||
      !pendingAutoSelectLoanId
    ) {
      return;
    }
    console.info('[LoanContext] NP-431/NP-600: adopting the first eligible loan:', pendingAutoSelectLoanId);
    isStaleIdClearingRef.current = false;
    setActiveLoanId(pendingAutoSelectLoanId);
    setIsFreshUser(false);
  }, [
    pendingAutoSelectLoanId,
    activeLoanId,
    isLoadingApps,
    pendingDraftLoanIdForOwner,
    incompleteAppPickId,
    setActiveLoanId,
  ]);

  // Enhanced loading logic: don't show loading forever for fresh users
  // If we've determined this is a fresh user with no loan data, stop loading.
  // Task 4 fix (2026-06-19 Tinker): also treat the window where incompleteApps has
  // returned loans but activeLoanId is not yet set as "loading" so the Dashboard
  // shows DashboardSkeleton instead of PROSPECT_NO_APP + Purchase/Refinance buttons.
  // Without this guard there is a 1-render flash of the loan type selection screen for
  // returning users whose localStorage was cleared on logout (commit 67ad0ce).
  // NP-431: also hold "loading" across the window where GET /api/v1/loans has
  // not settled yet, or has settled on a sole loan the auto-select effect is
  // about to adopt. Without this, loan-scoped pages render one frame with
  // loanId=undefined — which for Documents means a request to the
  // borrower-level endpoint and a visibly empty "Uploaded Documents".
  // Uses pendingAutoSelectLoanId (not autoSelectLoanId) so that after "Start a New
  // Loan" this is false and the gate settles — the invariant DOZER required:
  // sole loan + newLoanIntent => isLoading settles AND the wizard is reachable.
  // NP-617: the incomplete-apps term has to hold that same invariant, and a raw
  // `length > 0` could not — under a new-loan intent nothing will be adopted, so a
  // borrower with an in-progress application got a skeleton that never resolved.
  // `pendingIncompleteAppPickId` is the pick this gate is actually waiting for.
  const hasUnresolvedLoan =
    !activeLoanId &&
    !isFreshUser &&
    (!!pendingIncompleteAppPickId || isLoadingBorrowerLoans || !!pendingAutoSelectLoanId);
  /**
   * NP-529: a 404 on the PERSISTED loan id is recoverable, not a failure.
   *
   * `path-active-loan-id` deliberately outlives a logout (see applicationDraft.ts),
   * so the first read after login can be a loan this borrower cannot see — a DB
   * reset, GUID drift, or another account's id. The effect above discards it and
   * re-selects, which is why refreshing or switching tabs "fixed" it.
   *
   * What was missing is holding the UI across that window: the 404 reached the
   * Dashboard as `error`, and once the id was cleared the loan read resolved to
   * EMPTY_LOAN — so the borrower saw a broken dashboard for a state the app was
   * already repairing. Treat it as loading and swallow the error; if no reachable
   * loan remains, `hasUnresolvedLoan` goes false and the normal no-application
   * view renders, so this cannot hang.
   */
  const isRecoverableStale404 = (error as { status?: number } | null)?.status === 404;
  // NP-658: only the loan list says whether the selected loan is Encompass. Settling before it
  // lands lifecycles an Encompass loan as a Vesta application in progress — a hard load of
  // /my-loan bounced to "/" and the Vesta write actions flashed before the read-only flag.
  const isLosTypePending = Boolean(activeLoanId) && isLoadingBorrowerLoans;
  const isLoading =
    isLoadingApps || (isLoadingLoan && !isFreshUser) || hasUnresolvedLoan || isRecoverableStale404 || isLosTypePending;

  // NP-658: keyed on the SELECTED loan, never the header's loans[0] fallback — with no
  // selection there is nothing to protect, and a Vesta borrower must keep their actions.
  const isReadOnly = Boolean(
    activeLoanId &&
      borrowerLoansResult?.loans.some((l) => l.loanId === activeLoanId && l.losType === 'Encompass'),
  );

  // Derive LoanData from API result, or fall back to empty (no app)
  const apiLoan = useMemo<LoanData>(() => {
    let loanData: LoanData;
    
    // If we have a successful API result, use it — the server (loan status /
    // submittedAt) is the source of truth for submission state (Phase 3: the
    // localStorage submitted-application snapshot is retired).
    if (loanResult) {
      loanData = loanResultToLoanData(loanResult, { isEncompass: isReadOnly });
    }
    else {
      const persistedDraftLoan = draftToLoanData();
      loanData = persistedDraftLoan || EMPTY_LOAN;
    }

    return loanData;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanResult, error, submissionTrigger, isReadOnly]);

  // Effective loan: override takes priority (state switcher / demo mode)
  const loan = overrideLoan ?? apiLoan;
  const state = overrideState ?? computeLifecycle(loan);

  const setState = useCallback((s: LifecycleState) => {
    setOverrideState(s);
    // Only use seed data on localhost (dev/demo mode)
    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      import('@/lib/mockData').then(({ seedFor }) => setOverrideLoan(seedFor(s)));
    }
  }, []);

  const setLoan = useCallback((l: LoanData) => {
    setOverrideLoan(l);
    setOverrideState(null);
  }, []);

  const notifySubmission = useCallback(() => {
    // Clear overrides so fresh derivation from snapshot takes effect
    setOverrideLoan(null);
    setOverrideState(null);
    setSubmissionTrigger((n) => n + 1);
  }, []);

  const setActiveLoan = useCallback((id: string | undefined, options?: { explicit?: boolean }) => {
    // A loan the borrower picked themselves retires a pending "Start a New Loan".
    // Nothing else retires that marker but a successful create, so a click walked
    // away from leaves it standing for good — and while it stands
    // `readPersistedLoanId` discards the stored selection on the next load. The
    // borrower picked a loan, refreshed, and was unselected again, with the header
    // still naming a loan the app had not selected (LoanSwitcher falls back to
    // `loans[0]`) and every loan-scoped page rendering as though none existed.
    //
    // Only an explicit pick counts. The wizard's own adoption paths route through
    // here too, and suppressing them is the whole job of the marker — NP-585,
    // NP-614, NP-617 and NP-641 are each a path that adopted a loan the intent said
    // to leave alone.
    if (id && options?.explicit) {
      updateApplicationDraft((current) => ({ ...current, newLoanIntent: undefined }));
    }
    setActiveLoanId(id);
    // Clear overrides so the new loan data flows through
    setOverrideLoan(null);
    setOverrideState(null);
  }, [setActiveLoanId]);

  /**
   * NP-549 AC-5 — land the co-borrower on the loan their login just attached.
   *
   * Deliberately NOT setActiveLoan. That setter stamps the value with `sessionOwner`, which
   * is read during render: the caller runs in the continuation of `await login(...)`, before
   * React has re-rendered with the new auth user, so it would stamp the loan for the
   * PREVIOUS owner and NP-529's `readPersistedLoanId` would then correctly discard it. The
   * email that just authenticated is the only owner known to be right at this instant.
   *
   * Both writes are needed. The persisted one is what NP-529's owner-change branch adopts
   * when the account changed; the state one is what carries the selection when it did not —
   * an already-signed-in co-borrower re-authenticating through their own invite link.
   */
  const adoptLinkedLoan = useCallback((loanId: string, ownerEmail: string) => {
    persistLoanId(loanId, ownerOf(ownerEmail));
    setActiveLoanIdState(loanId);
    setOverrideLoan(null);
    setOverrideState(null);
  }, []);

  return (
    <LoanContext.Provider
      value={{
        loan,
        loanId: activeLoanId,
        loanResult: loanResult ?? undefined,
        state,
        setState,
        setLoan,
        setActiveLoan,
        adoptLinkedLoan,
        notifySubmission,
        isLoading,
        // NP-529: see isRecoverableStale404 — a stale persisted id is repaired, not reported.
        error: isRecoverableStale404 ? null : (error as Error | null),
        isReadOnly,
      }}
    >
      {children}
    </LoanContext.Provider>
  );
};

export const useLoan = () => {
  const ctx = useContext(LoanContext);
  if (!ctx) throw new Error("useLoan must be used inside LoanProvider");
  return ctx;
};

/**
 * NP-546: the active loan id, or undefined outside the provider.
 *
 * Non-throwing on purpose, like useAuthUserOptional: useLicensedStates needs the
 * loan to know which officer's licences apply, and it runs inside components that
 * are rendered without a LoanProvider (the Add Property modal in its own tests).
 * No loan is a legitimate state there — the licence filter falls back to the
 * captured `?lo=` address.
 */
export const useActiveLoanIdOptional = (): string | undefined =>
  useContext(LoanContext)?.loanId;

/** Stable identity so consumers outside a provider do not see a new function each render. */
const NO_LOAN_TO_ADOPT = () => {};

/**
 * NP-549: adopt the loan a login just attached, or a no-op outside the provider.
 *
 * Non-throwing for the same reason as useActiveLoanIdOptional: the Login page is mounted
 * without a LoanProvider by several existing tests whose subject is the form, not the loan
 * selection. App always supplies the provider, so the only no-op reachable in the running
 * app is none.
 */
export const useAdoptLinkedLoan = (): Ctx['adoptLinkedLoan'] =>
  useContext(LoanContext)?.adoptLinkedLoan ?? NO_LOAN_TO_ADOPT;
